import React from "react";
import { useParams } from "react-router-dom";
import { Copy, Loader2, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { getSafetyCopy } from "@/components/safety/safetyTranslations";

export default function SafetyLocationCard() {
  const { lang = "en" } = useParams();
  const copy = getSafetyCopy(lang).location;
  const [location, setLocation] = React.useState(null);
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!navigator.geolocation) {
      setError(copy.unavailable);
      setLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
        setLoading(false);
      },
      () => {
        setError(copy.allow);
        setLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
    );
  }, [copy.allow, copy.unavailable]);

  const coordinates = location
    ? `${location.latitude.toFixed(6)}, ${location.longitude.toFixed(6)}`
    : "";

  const copyCoordinates = () => {
    navigator.clipboard.writeText(coordinates);
    toast.success(copy.copied);
  };

  return (
    <Card className="border-primary/25 bg-primary/5">
      <CardContent className="p-4 md:p-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-start gap-3">
            <div className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-primary text-primary-foreground">
              <MapPin className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs font-black uppercase tracking-[0.2em] text-primary">{copy.title}</p>
              {loading ? (
                <p className="mt-2 flex items-center gap-2 text-sm font-semibold text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> {copy.loading}
                </p>
              ) : location ? (
                <>
                  <p className="mt-2 font-mono text-2xl font-black tracking-tight text-foreground md:text-3xl">{coordinates}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{copy.accuracy(Math.round(location.accuracy))}</p>
                </>
              ) : (
                <p className="mt-2 text-sm font-semibold text-muted-foreground">{error}</p>
              )}
            </div>
          </div>

          <Button type="button" variant="outline" onClick={copyCoordinates} disabled={!location} className="shrink-0">
            <Copy className="h-4 w-4" /> {copy.copy}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}